import type { ScheduleRow } from '../types/schedule.types';
import type { DateKey } from './dateKey';
import { formatMinorAmount } from './formatScheduleCurrency';
import { formatDateOnlyMonthYear, formatDateOnlyShort } from '@/app/utils/formatDate';
import { isRowZeroOnAnchor } from '../services/scheduleAnchor';

export interface ScheduleSummaryInput {
    readonly schedule: readonly ScheduleRow[];
    readonly anchorDate: DateKey | undefined;
    readonly today: DateKey;
    readonly currency: string;
    readonly depositRowConfirmed: boolean;
    readonly monthYearDates: boolean;
}

/** One aligned row of the schedule summary: what, when, how much. */
export interface ScheduleSummaryRow {
    readonly label: string;
    readonly when: string;
    readonly amount: string;
}

const money = (row: ScheduleRow, currency: string): string =>
    formatMinorAmount(row.payment, currency);

const date = (row: ScheduleRow, monthYear: boolean): string =>
    monthYear ? formatDateOnlyMonthYear(row.date) : formatDateOnlyShort(row.date);

const anchorRow = (row: ScheduleRow, input: ScheduleSummaryInput): ScheduleSummaryRow => {
    const exact = input.depositRowConfirmed || !input.monthYearDates;
    const when = date(row, !exact);
    return {
        label: input.depositRowConfirmed ? 'Deposit' : 'Payment',
        when: exact && input.anchorDate === input.today ? `${when} (today)` : when,
        amount: money(row, input.currency),
    };
};

const runsOfEqualPayment = (
    rows: readonly ScheduleRow[],
    currency: string,
    monthYear: boolean,
): ScheduleSummaryRow[] => {
    const summaryRows: ScheduleSummaryRow[] = [];
    let index = 0;
    while (index < rows.length) {
        let end = index;
        while (end + 1 < rows.length && rows[end + 1].payment === rows[index].payment) {
            end += 1;
        }
        const runLength = end - index + 1;
        summaryRows.push({
            label: `${runLength} payment${runLength === 1 ? '' : 's'}`,
            // A run of one falls on a single date, so "until" would overstate it.
            when: runLength === 1 ? date(rows[end], monthYear) : `until ${date(rows[end], monthYear)}`,
            amount: money(rows[index], currency),
        });
        index = end + 1;
    }
    return summaryRows;
};

// Derived from `schedule` rows alone: no scheduling-method branch, so the
// rows stay correct for every scheduling method (AC1).
export const buildScheduleSummaryRows = (input: ScheduleSummaryInput): ScheduleSummaryRow[] => {
    const { schedule, currency } = input;
    if (schedule.length === 0) {
        return [];
    }

    const summaryRows: ScheduleSummaryRow[] = [];
    const rowZeroIsAnchor = isRowZeroOnAnchor(schedule, input.anchorDate);
    const rest = rowZeroIsAnchor ? schedule.slice(1) : schedule;

    if (rowZeroIsAnchor) {
        summaryRows.push(anchorRow(schedule[0], input));
        if (rest.length === 0) {
            return summaryRows;
        }
    }

    const finalRow = rest[rest.length - 1];
    const middle = rest.slice(0, -1);
    summaryRows.push(...runsOfEqualPayment(middle, currency, input.monthYearDates));
    summaryRows.push({
        label: 'Final payment',
        when: date(finalRow, input.monthYearDates),
        amount: money(finalRow, currency),
    });

    return summaryRows;
};
